import { HttpInterceptorFn } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { AuthService } from "../services/auth.service";
import { BehaviorSubject, catchError, filter, finalize, switchMap, take, throwError } from "rxjs";

let isRefreshing = false;
const refreshSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);

    const token = authService.getToken();

    if (
        req.url.startsWith('https://storage.googleapis.com') ||
        req.url.includes('googleapis.com')
    ) {
        return next(req);
    }

    const authReq = token ? req.clone({
        setHeaders: {
            Authorization: `Bearer ${token}`,
        },
    }) : req;

    return next(authReq).pipe(
        catchError((error) => {
            if(
                error.status !== 401 ||
                req.url.includes('/auth/login') ||
                req.url.includes('/auth/refresh')
            ) {
                return throwError(() => error);
            }

            if (!isRefreshing) {
                isRefreshing = true;
                refreshSubject.next(null);

                return authService.refreshToken().pipe(
                    switchMap(tokens => {
                        refreshSubject.next(tokens.accessToken);

                        return next(
                            req.clone({
                                setHeaders: {
                                    Authorization: `Bearer ${tokens.accessToken}`
                                }
                            })
                        );
                    }),
                    catchError(refreshError => {
                        authService.logout();
                        return throwError(() => refreshError);
                    }),
                    finalize(() => {
                        isRefreshing = false;
                    })
                );
            }

            return refreshSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token =>
                    next(
                        req.clone({
                            setHeaders: {
                                Authorization: `Bearer ${token}`
                            }
                        })
                    )
                )
            );
        })
    );
}